import { useCallback, useEffect, useState } from 'react';

import { useStripe } from '@stripe/react-stripe-js';
import type { PaymentRequest } from '@stripe/stripe-js';

type Params = {
	amount: number;
	currencyCode: string;
	label: string;
};

export function usePaymentRequest({ amount, currencyCode, label }: Params): PaymentRequest | null {
	const stripe = useStripe();
	const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);

	const createPaymentRequest = useCallback(async () => {
		if (!stripe) return;

		const newPaymentRequest = stripe.paymentRequest({
			// The Payment Request object must be initialized with our Stripe account's country code
			country: 'US',
			currency: currencyCode.toLowerCase(),
			total: {
				label,
				amount,
			},
			requestPayerName: true,
			requestPayerEmail: true,
		});

		if (await newPaymentRequest.canMakePayment()) {
			setPaymentRequest(newPaymentRequest);
		}
	}, [amount, currencyCode, label, stripe]);

	// Keep the PaymentRequest updated with the latest amount and label
	useEffect(() => {
		if (!paymentRequest) return;

		try {
			paymentRequest.update({
				total: {
					amount,
					label,
				},
			});
		} catch (error) {
			// If anything goes wrong, clear the payment request so another will be created with the current data.

			// This failure happens if a guest user starts a payment, is prompted to log in, then cancels the login modal and adjusts the amount.
			// PaymentRequest's update() can only be called when the browser payment interface is not showing:
			// https://docs.stripe.com/js/payment_request/update

			// We don't call abort() on the old PaymentRequest here because it's unsafe: It may throw InvalidAccessError based on the state of the PaymentRequest.

			// eslint-disable-next-line react-hooks/set-state-in-effect
			setPaymentRequest(null);
		}
	}, [amount, label, paymentRequest]);

	useEffect(() => {
		if (stripe && !paymentRequest) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			void createPaymentRequest();
		}
	}, [createPaymentRequest, paymentRequest, stripe]);

	return paymentRequest;
}
